import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import { trackCookiConsentEvent } from "@tKgpco/utilities/tracking-kgpco";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    acceptGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
    declineButton?: ButtonPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        // size: 100,
        isAutoSize: true,
        cssOverrides: {
            scrim: css`
                height: 100px;
                > div[class^="ContentContainer"] {
                    width: 100%;
                }
            `,
            drawerContainer: css`
                & > div[class^="ContentBody"] {
                    display: block;
                    min-height: 100px;
                    height: auto;
                    width: 100%;
                    overflow-x: unset;
                    overflow-y: unset;
                }
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 15px;
                margin: 0 auto;
                width: 100%;
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            margin: 0 auto;
        `,
    },
    messageGridItem: {
        width: [11, 8, 9, 9, 9],
        css: css`
            align-items: center;
        `,
    },
    acceptGridItem: {
        width: [12, 4, 3, 3, 3],
        css: css`
            justify-content: center;
            @media (max-width: 1199px) and (min-width: 768px) {
                justify-content: flex-start;
            }
        `,
    },
    acceptButton: {
        variant: "primary",
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            margin-right: 0.5rem;
            text-transform: uppercase;
            border-radius: 22px;
            background-image: linear-gradient(to right, #44a8f7, ${COLORS.link.base});
            border: none;
            width: auto;
            color: ${COLORS.white};
            & :hover {
                background: ${COLORS.secondaryBtn};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
            }
        `,
    },
    // closeGridItem: {
    //     width: [4, 3, 2, 1, 1],
    // },
    closeButton: {
        variant: "secondary",
        css: css`
            padding: 0;
            background: transparent;
            border: none;
            position: absolute;
            right: 1rem;
            top: 0.5rem;
            & > span {
                svg {
                    color: ${COLORS.black};
                }
            }
            & :hover {
                background: transparent;
            }
        `,
    },
    declineButton: {
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            text-transform: uppercase;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            background: ${COLORS.white};
            color: ${COLORS.secondaryBtn};
            width: auto;
            & :hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
                border: 1px solid ${COLORS.disabled.base};
            }
            > span {
                color: ${COLORS.link.base} !important;
            }
        `,
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "true", { expires: 2147483647 });
        trackCookiConsentEvent(true);
    };

    const declineHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "false", { expires: 2147483647 });
        trackCookiConsentEvent(false);
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <GridContainer {...styles.container}>
                <GridItem {...styles.messageGridItem}>
                    <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                        {siteMessage("Core_CookiePrivacyPolicy")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.acceptGridItem}>
                    <Button
                        {...styles.acceptButton}
                        onClick={acceptHandler}
                        data-test-selector="cookiePolicy_AcceptCookies"
                    >
                        {siteMessage("Core_CookiePrivacyPolicyAccept")}
                    </Button>
                    <Button {...styles.declineButton} onClick={declineHandler}>
                        {siteMessage("Core_CookiePrivacyPolicyDecline")}
                    </Button>
                </GridItem>
            </GridContainer>
            <Button
                {...styles.closeButton}
                onClick={closeHandler}
                aria-label={translate("Close")}
                data-test-selector="cookiePolicy_ClosePolicy"
            >
                <ButtonIcon src={X} />
            </Button>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
